import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Send,
  Shield,
  Clock,
  AlertTriangle,
  MessageSquare,
  Calendar,
  User as UserIcon,
} from "lucide-react";

interface Ticket {
  id: number;
  status: string;
  subject: string;
  user: string;
  description: string;
  category: string;
  priority: string;
  createdAt: string;
  messages: Message[];
}

interface Message {
  id: number;
  sender: string;
  message: string;
  timestamp: string;
}

export function TicketDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const ticket = location.state?.ticket as Ticket;

  const [newMessage, setNewMessage] = useState("");
  const [currentTicket, setCurrentTicket] = useState<Ticket | null>(ticket);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!currentTicket) {
      // If no ticket in state, redirect back to help support
      navigate("/help-support");
      return;
    }
    scrollToBottom();
  }, [currentTicket?.messages, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "urgent":
        return <Badge className="bg-red-100 text-red-800 border-red-200">Urgent</Badge>;
      case "high":
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-200">High</Badge>
        );
      case "medium":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            Medium
          </Badge>
        );
      case "low":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">Low</Badge>
        );
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Open</Badge>;
      case "in_progress":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            In Progress
          </Badge>
        );
      case "resolved":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">Resolved</Badge>
        );
      case "closed":
        return (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200">Closed</Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case "technical":
        return (
          <Badge
            variant="secondary"
            className="bg-purple-50 text-purple-700 border-purple-200"
          >
            Technical
          </Badge>
        );
      case "billing":
        return (
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
            Billing
          </Badge>
        );
      case "account":
        return (
          <Badge
            variant="secondary"
            className="bg-green-50 text-green-700 border-green-200"
          >
            Account
          </Badge>
        );
      case "general":
        return (
          <Badge variant="secondary" className="bg-gray-50 text-gray-700 border-gray-200">
            General
          </Badge>
        );
      case "bug":
        return (
          <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200">
            Bug
          </Badge>
        );
      case "feature":
        return (
          <Badge
            variant="secondary"
            className="bg-orange-50 text-orange-700 border-orange-200"
          >
            Feature
          </Badge>
        );
      default:
        return <Badge variant="outline">{category}</Badge>;
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && currentTicket) {
      const newMsg = {
        id: currentTicket.messages.length + 1,
        sender: "Admin",
        message: newMessage.trim(),
        timestamp: new Date().toISOString(),
      };
      const updatedTicket = {
        ...currentTicket,
        messages: [...currentTicket.messages, newMsg],
      };
      setCurrentTicket(updatedTicket);
      setNewMessage("");
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!currentTicket) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigate("/help-support");
                  }}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tickets
                </Button>
                <Separator orientation="vertical" className="h-6" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Ticket #{currentTicket.id}
              </h1>
              <p className="text-gray-600 mt-1">
                {currentTicket.subject} • {currentTicket.user}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getPriorityBadge(currentTicket.priority)}
              {getStatusBadge(currentTicket.status)}
              {getCategoryBadge(currentTicket.category)}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-4">
            {/* Ticket Information Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Ticket Details */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-gray-900">Ticket Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Subject</label>
                    <p className="text-sm font-medium mt-1 text-gray-900">
                      {currentTicket.subject}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <p className="text-sm text-gray-600 mt-1 bg-gray-50 p-3 rounded-lg border border-gray-200">
                      {currentTicket.description}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* User Information */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-gray-900">Customer</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 border-blue-100">
                      <AvatarFallback className="text-sm bg-blue-50 text-blue-600">
                        {currentTicket.user
                          .split(" ")
                          .map((n: string) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-gray-900">{currentTicket.user}</p>
                      <p className="text-sm text-gray-500">Customer</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-gray-900">Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">
                      {new Date(currentTicket.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MessageSquare className="h-4 w-4 text-green-500" />
                    <span className="text-gray-600">Messages:</span>
                    <span className="text-gray-900">{currentTicket.messages.length}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-orange-500" />
                    <span className="text-gray-600">Last updated:</span>
                    <span className="text-gray-900">
                      {new Date(
                        currentTicket.messages[currentTicket.messages.length - 1]
                          ?.timestamp || currentTicket.createdAt
                      ).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-gray-900">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-gray-200 hover:bg-blue-50 hover:border-blue-200"
                    onClick={() => {
                      console.log("Update status");
                    }}
                  >
                    <Clock className="h-4 w-4 mr-2 text-blue-500" />
                    Update Status
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-gray-200 hover:bg-green-50 hover:border-green-200"
                    onClick={() => {
                      console.log("Assign ticket");
                    }}
                  >
                    <UserIcon className="h-4 w-4 mr-2 text-green-500" />
                    Assign Ticket
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-gray-200 hover:bg-orange-50 hover:border-orange-200"
                    onClick={() => {
                      console.log("Change priority");
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
                    Change Priority
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-3">
              <Card className="h-[600px] flex flex-col border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="border-b border-gray-200 pb-4">
                  <CardTitle className="text-lg text-gray-900">Conversation</CardTitle>
                </CardHeader>

                {/* Messages */}
                <div className="flex-1 overflow-hidden">
                  <ScrollArea className="h-full p-6">
                    <div className="space-y-6">
                      {currentTicket.messages.map((msg: Message, index: number) => {
                        const isAdmin = msg.sender === "Admin";
                        const showDate =
                          index === 0 ||
                          formatDate(currentTicket.messages[index - 1].timestamp) !==
                            formatDate(msg.timestamp);

                        return (
                          <div key={msg.id}>
                            {showDate && (
                              <div className="flex justify-center my-6">
                                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full border border-gray-200">
                                  {formatDate(msg.timestamp)}
                                </span>
                              </div>
                            )}

                            <div
                              className={`flex gap-4 ${
                                isAdmin ? "justify-end" : "justify-start"
                              }`}
                            >
                              {!isAdmin && (
                                <Avatar className="h-10 w-10 flex-shrink-0 border-2 border-gray-100">
                                  <AvatarFallback className="text-sm bg-gray-50 text-gray-600">
                                    {msg.sender
                                      .split(" ")
                                      .map((n: string) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>
                              )}

                              <div
                                className={`max-w-[70%] ${isAdmin ? "order-first" : ""}`}
                              >
                                <div
                                  className={`rounded-2xl px-4 py-3 shadow-sm ${
                                    isAdmin
                                      ? "bg-blue-600 text-white"
                                      : "bg-gray-100 text-gray-900 border border-gray-200"
                                  }`}
                                >
                                  <p className="text-sm leading-relaxed">{msg.message}</p>
                                </div>
                                <div
                                  className={`flex items-center gap-2 mt-2 ${
                                    isAdmin ? "justify-end" : "justify-start"
                                  }`}
                                >
                                  <span className="text-xs text-gray-500">
                                    {formatTime(msg.timestamp)}
                                  </span>
                                  {isAdmin && (
                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                      <Shield className="h-3 w-3" />
                                      <span>Admin</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {isAdmin && (
                                <Avatar className="h-10 w-10 flex-shrink-0 border-2 border-blue-100">
                                  <AvatarFallback className="text-sm bg-blue-50 text-blue-600">
                                    <Shield className="h-5 w-5" />
                                  </AvatarFallback>
                                </Avatar>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </div>

                {/* Message Input */}
                <div className="border-t border-gray-200 p-6 bg-gray-50/50">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Textarea
                        ref={textareaRef}
                        placeholder="Type your response... (Press Enter to send, Shift+Enter for new line)"
                        value={newMessage}
                        onChange={handleTextareaChange}
                        onKeyPress={handleKeyPress}
                        rows={2}
                        className="resize-none min-h-[60px] max-h-[120px] border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="self-end px-6 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </Button>
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <p className="text-xs text-gray-500">
                      Press Enter to send, Shift+Enter for new line
                    </p>
                    {newMessage.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {newMessage.length} characters
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
